/**
 * Everpret cart-drawer presentation controller.
 *
 * Owns no cart/reward business logic itself — wires CartStore, the
 * RewardSynchronizer, RecommendationEngine and CheckoutManager into the
 * cart-drawer markup. Runs after every render of the drawer's section HTML
 * (initial load, and every `cart-drawer:refreshed` the existing <cart-drawer>
 * element already dispatches after its Section-Rendering-API replaceChildren
 * pass), so it never fights the theme's existing re-render mechanism.
 */
(function (window, document) {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    var Everpret = window.Everpret || {};
    var cartStore = Everpret.cartStore;
    if (!cartStore) return;

    var rewardSynchronizer = new Everpret.RewardSynchronizer(cartStore);
    var checkoutManager = new Everpret.CheckoutManager(rewardSynchronizer);
    Everpret.rewardSynchronizer = rewardSynchronizer;
    Everpret.checkoutManager = checkoutManager;

    function drawerRoot() {
      return document.getElementById('cart-drawer');
    }

    function readRewardState() {
      var drawer = drawerRoot();
      var node = drawer && drawer.querySelector('[data-reward-state]');
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (e) {
        return null;
      }
    }

    function renderRecommendations() {
      var drawer = drawerRoot();
      if (!drawer) return;
      var container = drawer.querySelector('[data-recommendations]');
      if (!container || !Everpret.RecommendationEngine) return;

      var state = readRewardState();
      var amountRemaining = state && state.nextReward ? state.nextReward.amountRemaining : 0;
      var cart = cartStore.cart;
      var picks = Everpret.RecommendationEngine.select(drawer, cart, amountRemaining, 6);

      container.innerHTML = picks
        .map(function (p) {
          return (
            '<div class="rec">' +
            '<div class="rec__img"><img src="' + p.image + '" alt="' + escapeHtml(p.title) + '" loading="lazy"></div>' +
            '<div class="rec__b">' +
            '<div class="rec__name">' + escapeHtml(p.title) + '</div>' +
            '<div class="rec__price">' + p.priceFormatted + '</div>' +
            '<button type="button" class="rec__add" data-rec-add="' + p.variantId + '">+ Add</button>' +
            '</div></div>'
          );
        })
        .join('');
    }

    function escapeHtml(str) {
      return String(str || '').replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    function refresh() {
      rewardSynchronizer.sync(drawerRoot()).finally(renderRecommendations);
    }

    // Delegated interactions — attached once on document since drawer content
    // is replaced wholesale on every re-render.
    document.addEventListener('click', function (event) {
      var recAdd = event.target.closest && event.target.closest('[data-rec-add]');
      if (recAdd) {
        recAdd.disabled = true;
        cartStore
          .addItems([{ id: Number(recAdd.dataset.recAdd), quantity: 1 }], { baseEvent: 'recommendation:add' })
          .then(function () {
            recAdd.textContent = 'Added';
            recAdd.classList.add('added');
          })
          .catch(function () {
            recAdd.disabled = false;
          });
        return;
      }

      var addonToggle = event.target.closest && event.target.closest('[data-addon-toggle]');
      if (addonToggle) {
        var variantId = Number(addonToggle.dataset.addonVariantId);
        var isOn = addonToggle.classList.contains('on');
        addonToggle.setAttribute('aria-checked', String(!isOn));
        addonToggle.classList.toggle('on', !isOn);

        if (!isOn) {
          cartStore.addItems(
            [{ id: variantId, quantity: 1, properties: { _addon: 'true', _addon_id: addonToggle.dataset.addonId || '' } }],
            { baseEvent: 'addon:add' }
          );
        } else {
          var cart = cartStore.cart;
          var line = cart && (cart.items || []).find(function (l) {
            return l.properties && l.properties._addon_id === addonToggle.dataset.addonId;
          });
          if (line) cartStore.removeItem(line.key, { baseEvent: 'addon:remove' });
        }
        return;
      }

      var discountApply = event.target.closest && event.target.closest('[data-discount-apply]');
      if (discountApply) {
        // No custom apply logic here — the theme's own <cart-discount-field>
        // already applies on the input's native "change" event (fires on blur
        // or Enter). This button is a visual affordance only: it dispatches
        // that same event so clicking it reuses the existing, untouched apply
        // flow instead of duplicating it.
        var field = discountApply.closest('.discount-card');
        var input = field && field.querySelector('cart-discount-field input');
        if (input) input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      var checkoutTrigger = event.target.closest && event.target.closest('[data-checkout-trigger]');
      // Fast path: no reward sync in flight — let the native form submission
      // proceed untouched (preserves the "checkout" submit button's name/value,
      // which Shopify's cart->checkout redirect depends on).
      if (checkoutTrigger && checkoutManager.hasPendingSync()) {
        event.preventDefault();
        var form = checkoutTrigger.closest('form');
        checkoutManager.checkout(cartStore.cart, form, checkoutTrigger);
      }
    });

    document.addEventListener('keydown', function (event) {
      var addonToggle = event.target.closest && event.target.closest('[data-addon-toggle]');
      if (addonToggle && (event.key === ' ' || event.key === 'Enter')) {
        event.preventDefault();
        addonToggle.click();
      }
    });

    document.addEventListener('change', function (event) {
      var select = event.target.closest && event.target.closest('[data-variant-change]');
      if (!select) return;
      var lineKey = select.dataset.variantChange;
      var newVariantId = Number(select.value);
      var cart = cartStore.cart;
      var line = cart && (cart.items || []).find(function (l) {
        return l.key === lineKey;
      });
      if (!line) return;
      select.disabled = true;
      cartStore
        .changeVariant(lineKey, newVariantId, line.quantity, line.properties, { baseEvent: 'cart-store:variant-change' })
        .finally(function () {
          select.disabled = false;
        });
    });

    document.addEventListener('cart-drawer:refreshed', refresh);
    document.addEventListener('cart:change', refresh);
    cartStore.fetchCart().then(refresh);
  });
})(window, document);
